import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../supabaseClient'

const EMPTY_FORM = {
  marca: '',
  modelo: '',
  potencia: '',
  qtd_strings: '',
  qtd_mppt: '',
  pmax: '',
  vccmax: '',
  iccmax: '',
  vpmpmax: '',
  vpmpmin: '',
  vccpart: '',
  qtd_mppt_entrada: '',
  pcamax: '',
  imaxca: '',
  vcamax: '',
  vcamin: '',
  thd: '',
  fator: '',
  eficiencia: ''
}

const FIELDS = [
  { key: 'marca', label: 'Marca', type: 'text', required: true },
  { key: 'modelo', label: 'Modelo', type: 'text', required: true },
  { key: 'potencia', label: 'Potência (kW)', type: 'number' },
  { key: 'qtd_strings', label: 'Qtd. Strings', type: 'integer' },
  { key: 'qtd_mppt', label: 'Qtd. MPPT', type: 'integer' },
  { key: 'pmax', label: 'PMAX', type: 'number' },
  { key: 'vccmax', label: 'VCCMAX', type: 'number' },
  { key: 'iccmax', label: 'ICCMAX', type: 'number' },
  { key: 'vpmpmax', label: 'VPMPMAX', type: 'number' },
  { key: 'vpmpmin', label: 'VPMPMIN', type: 'number' },
  { key: 'vccpart', label: 'VCCPART', type: 'number' },
  { key: 'qtd_mppt_entrada', label: 'QTD_MPPT (entrada)', type: 'integer' },
  { key: 'pcamax', label: 'PCAMAX', type: 'number' },
  { key: 'imaxca', label: 'IMAXCA', type: 'number' },
  { key: 'vcamax', label: 'VCAMAX', type: 'number' },
  { key: 'vcamin', label: 'VCAMIN', type: 'number' },
  { key: 'thd', label: 'THD (%)', type: 'number' },
  { key: 'fator', label: 'Fator de potência', type: 'number' },
  { key: 'eficiencia', label: 'Eficiência (%)', type: 'number' }
]

const toNumberOrNull = (value, type) => {
  if (value == null) return null
  const raw = String(value).trim().replace(',', '.')
  if (!raw) return null
  const parsed = type === 'integer' ? parseInt(raw, 10) : parseFloat(raw)
  return Number.isNaN(parsed) ? null : parsed
}

export default function EngenhariaInversores({ onBack }) {
  const [lista, setLista] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [filtroMarca, setFiltroMarca] = useState('')
  const [filtroModelo, setFiltroModelo] = useState('')
  const [form, setForm] = useState(EMPTY_FORM)
  const [msg, setMsg] = useState({ type: '', text: '' })

  const loadLista = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('inversores')
        .select('*')
        .order('marca')
        .order('modelo')
      if (error) throw error
      setLista(data || [])
    } catch (e) {
      setMsg({ type: 'danger', text: `Erro ao carregar inversores: ${e.message}` })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadLista()
  }, [loadLista])

  useEffect(() => {
    if (!showModal) return undefined
    document.body.classList.add('modal-open')
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.classList.remove('modal-open')
      document.body.style.overflow = previousOverflow
    }
  }, [showModal])

  const filtrados = useMemo(() => {
    const marcaBusca = filtroMarca.trim().toLowerCase()
    const modeloBusca = filtroModelo.trim().toLowerCase()
    return lista.filter((inv) => {
      const marca = (inv.marca || '').toLowerCase()
      const modelo = (inv.modelo || '').toLowerCase()
      if (marcaBusca && !marca.includes(marcaBusca)) return false
      if (modeloBusca && !modelo.includes(modeloBusca)) return false
      return true
    })
  }, [lista, filtroMarca, filtroModelo])

  const openCadastrarModal = () => {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setShowModal(true)
  }

  const openEditar = (row) => {
    setEditingId(row.id)
    const next = {}
    FIELDS.forEach((f) => {
      next[f.key] = row[f.key] == null ? '' : String(row[f.key])
    })
    setForm(next)
    setShowModal(true)
  }

  const closeModal = () => {
    setShowModal(false)
    setEditingId(null)
    setForm(EMPTY_FORM)
  }

  const handleSave = async () => {
    if (!form.marca.trim() || !form.modelo.trim()) {
      setMsg({ type: 'warning', text: 'Marca e modelo são obrigatórios.' })
      return
    }

    const payload = {}
    FIELDS.forEach((field) => {
      if (field.type === 'text') {
        payload[field.key] = form[field.key].trim()
      } else {
        payload[field.key] = toNumberOrNull(form[field.key], field.type)
      }
    })

    try {
      setSaving(true)
      if (editingId) {
        const { error } = await supabase.from('inversores').update(payload).eq('id', editingId)
        if (error) throw error
        setMsg({ type: 'success', text: 'Inversor atualizado com sucesso.' })
      } else {
        const { error } = await supabase.from('inversores').insert([payload])
        if (error) throw error
        setMsg({ type: 'success', text: 'Inversor cadastrado com sucesso.' })
      }
      closeModal()
      await loadLista()
    } catch (e) {
      setMsg({ type: 'danger', text: `Erro ao salvar: ${e.message}` })
    } finally {
      setSaving(false)
    }
  }

  const modalContent = showModal
    ? createPortal(
      <>
        <div
          className="modal fade show"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          style={{ display: 'block', zIndex: 1060 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal()
          }}
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Editar inversor' : 'Novo inversor'}</h5>
                <button type="button" className="btn-close" onClick={closeModal} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  {FIELDS.map((field) => (
                    <div className="col-12 col-md-4" key={field.key}>
                      <label className="form-label">{field.label}</label>
                      <input
                        type={field.type === 'text' ? 'text' : 'text'}
                        inputMode={field.type === 'integer' || field.type === 'number' ? 'decimal' : 'text'}
                        className="form-control"
                        value={form[field.key]}
                        onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                        placeholder={field.type === 'text' ? '' : 'Ex.: 6,3'}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeModal} disabled={saving}>
                  Cancelar
                </button>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-backdrop fade show" style={{ zIndex: 1055 }} />
      </>,
      document.body
    )
    : null

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-outline-secondary" onClick={onBack}>
            ← Voltar
          </button>
          <h2 className="m-0">🏗️ Engenharia - Inversores</h2>
        </div>
        <button type="button" className="btn btn-primary" onClick={openCadastrarModal}>
          Cadastrar
        </button>
      </div>

      {msg.text ? <div className={`alert alert-${msg.type}`}>{msg.text}</div> : null}

      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label">Filtro por Marca</label>
              <input
                className="form-control"
                placeholder="Digite a marca..."
                value={filtroMarca}
                onChange={(e) => setFiltroMarca(e.target.value)}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">Filtro por Modelo</label>
              <input
                className="form-control"
                placeholder="Digite o modelo..."
                value={filtroModelo}
                onChange={(e) => setFiltroModelo(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <p className="m-0">Carregando...</p>
          ) : filtrados.length === 0 ? (
            <p className="m-0">Nenhum inversor encontrado.</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover align-middle">
                <thead>
                  <tr>
                    <th>Marca</th>
                    <th>Modelo</th>
                    <th>Potência</th>
                    <th>Strings</th>
                    <th>MPPT</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtrados.map((row) => (
                    <tr key={row.id}>
                      <td>{row.marca || '—'}</td>
                      <td>{row.modelo || '—'}</td>
                      <td>{row.potencia ?? '—'}</td>
                      <td>{row.qtd_strings ?? '—'}</td>
                      <td>{row.qtd_mppt ?? '—'}</td>
                      <td>
                        <button className="btn btn-sm btn-outline-primary" onClick={() => openEditar(row)}>
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalContent}
    </div>
  )
}

